import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, number, strictObject, string } from "../../lazy-zod.js";
import { agentControllerCollaborationModeSchema } from "../agent-controller-collaboration-mode.js";
import { agentControllerPermissionModeSchema } from "../agent-controller-permission-mode.js";
import { openCodePermissionConfigSchema } from "../open-code-permission.js";
import { agentActivityNativeSessionIdSchema, localAgentSessionProviderSchema } from "./agent-activity-sessions.js";
//#region src/schemas/http/agent-profile-binding.ts
const boundedIdentifierSchema = string().trim().min(1).max(256);
const reasonCodeSchema = string().trim().min(1).max(256);
const agentProfileBindingRouteParamsSchema = strictObject({ agentProfileId: boundedIdentifierSchema });
const currentAgentProfileSessionLookupQuerySchema = strictObject({
	agentControllerConversationId: agentActivityNativeSessionIdSchema,
	agentControllerRef: boundedIdentifierSchema
});
const currentAgentProfileSessionLookupIdentitySchema = strictObject({
	profileId: boundedIdentifierSchema,
	profileName: boundedIdentifierSchema,
	profileOrganizationId: boundedIdentifierSchema
});
const currentAgentProfileSessionLookupResponseSchema = discriminatedUnion("status", [
	strictObject({
		currentOrganizationId: boundedIdentifierSchema,
		status: literal("not_found")
	}),
	strictObject({
		currentOrganizationId: boundedIdentifierSchema,
		profile: currentAgentProfileSessionLookupIdentitySchema,
		sameOrganization: boolean(),
		status: literal("found")
	}),
	strictObject({
		currentOrganizationId: boundedIdentifierSchema,
		reasonCode: reasonCodeSchema,
		status: literal("unavailable")
	})
]);
const getAgentProfileControllerChangeEligibilityResponseSchema = discriminatedUnion("status", [
	strictObject({
		agentProfileId: boundedIdentifierSchema,
		reasonCode: literal("agent_profile_binding.controller_change_allowed"),
		status: literal("allowed")
	}),
	strictObject({
		agentProfileId: boundedIdentifierSchema,
		reasonCode: literal("agent_profile_binding.controller_change_blocked_active_execution"),
		status: literal("blocked")
	}),
	strictObject({
		agentProfileId: boundedIdentifierSchema,
		reasonCode: reasonCodeSchema,
		status: literal("unavailable")
	})
]);
const agentProfileBindingRuntimeIdSchema = strictObject({
	source: literal("runtime_registry_authority"),
	value: string().trim().min(1).max(512)
});
const agentProfileBindingModelSelectionSchema = strictObject({
	modelId: boundedIdentifierSchema,
	reasoningLevel: boundedIdentifierSchema.optional()
});
const agentProfileBindingAgentControllerPermissionModeSchema = agentControllerPermissionModeSchema;
const agentProfileBindingAgentControllerCollaborationModeSchema = agentControllerCollaborationModeSchema;
const agentProfileBindingOpenCodePermissionSchema = openCodePermissionConfigSchema;
const agentProfileBindingWriteModeSchema = enumFactory(["ensure", "reconnect"]);
const agentProfileBindingSessionInventorySelectionSchema = strictObject({
	computerId: boundedIdentifierSchema,
	nativeSessionId: agentActivityNativeSessionIdSchema,
	organizationId: boundedIdentifierSchema,
	ownerUserId: boundedIdentifierSchema,
	projectId: boundedIdentifierSchema,
	provider: localAgentSessionProviderSchema
});
const ensureAgentProfilePrimaryBindingBodySchema = strictObject({
	agentControllerRef: boundedIdentifierSchema,
	agentControllerConversationId: agentActivityNativeSessionIdSchema.optional(),
	deviceId: boundedIdentifierSchema,
	lane: boundedIdentifierSchema,
	openMeldServiceRuntimeId: agentProfileBindingRuntimeIdSchema,
	agentControllerCollaborationMode: agentProfileBindingAgentControllerCollaborationModeSchema.optional(),
	agentControllerPermissionMode: agentProfileBindingAgentControllerPermissionModeSchema.optional(),
	modelId: boundedIdentifierSchema.optional(),
	openCodePermission: agentProfileBindingOpenCodePermissionSchema.optional(),
	reasoningLevel: boundedIdentifierSchema.optional(),
	sessionInventorySelection: agentProfileBindingSessionInventorySelectionSchema.optional(),
	writeMode: agentProfileBindingWriteModeSchema.optional()
}).superRefine((value, context) => {
	if (value.reasoningLevel && !value.modelId) context.addIssue({
		code: "custom",
		message: "reasoningLevel requires modelId",
		path: ["reasoningLevel"]
	});
	if (value.sessionInventorySelection && value.agentControllerConversationId !== value.sessionInventorySelection.nativeSessionId) context.addIssue({
		code: "custom",
		message: "sessionInventorySelection requires the matching agentControllerConversationId",
		path: ["sessionInventorySelection", "nativeSessionId"]
	});
});
const updateAgentProfilePrimaryBindingModelSelectionBodySchema = strictObject({
	modelId: boundedIdentifierSchema,
	reasoningLevel: boundedIdentifierSchema.optional()
}).superRefine((value, context) => {
	if (value.reasoningLevel && !value.modelId) context.addIssue({
		code: "custom",
		message: "reasoningLevel requires modelId",
		path: ["reasoningLevel"]
	});
});
const updateAgentProfilePrimaryBindingAgentControllerPermissionModeBodySchema = strictObject({ agentControllerPermissionMode: agentProfileBindingAgentControllerPermissionModeSchema });
const updateAgentProfilePrimaryBindingAgentControllerCollaborationModeBodySchema = strictObject({ agentControllerCollaborationMode: agentProfileBindingAgentControllerCollaborationModeSchema });
const updateAgentProfilePrimaryBindingOpenCodePermissionBodySchema = strictObject({ openCodePermission: agentProfileBindingOpenCodePermissionSchema.nullable() });
const agentProfileCreationDefaultsFields = {
	agentControllerCollaborationMode: agentControllerCollaborationModeSchema.nullable(),
	agentControllerPermissionMode: agentControllerPermissionModeSchema.nullable(),
	defaultProjectId: boundedIdentifierSchema.nullable(),
	modelId: boundedIdentifierSchema.nullable(),
	openCodePermission: openCodePermissionConfigSchema.nullable(),
	reasoningLevel: boundedIdentifierSchema.nullable()
};
function requireCreationDefaultsModelForReasoning(value, context) {
	if (value.reasoningLevel && !value.modelId) context.addIssue({
		code: "custom",
		message: "reasoningLevel requires modelId",
		path: ["reasoningLevel"]
	});
}
const agentProfileCreationPreferenceSelectionSchema = strictObject(agentProfileCreationDefaultsFields).superRefine(requireCreationDefaultsModelForReasoning);
const setAgentProfileCreationDefaultsBodySchema = strictObject({
	...agentProfileCreationDefaultsFields,
	agentControllerCollaborationMode: agentControllerCollaborationModeSchema.nullable().optional()
}).superRefine(requireCreationDefaultsModelForReasoning);
const agentProfileCreationDefaultsSchema = strictObject({
	...agentProfileCreationDefaultsFields,
	agentControllerRef: boundedIdentifierSchema,
	organizationId: boundedIdentifierSchema,
	updatedAt: iso.datetime({ offset: true }).nullable(),
	userId: boundedIdentifierSchema
}).superRefine(requireCreationDefaultsModelForReasoning);
const getAgentProfileCreationDefaultsResponseSchema = agentProfileCreationDefaultsSchema;
const setAgentProfileCreationDefaultsResponseSchema = agentProfileCreationDefaultsSchema;
const listAgentProfileCreationDefaultsResponseSchema = strictObject({
	defaults: array(agentProfileCreationDefaultsSchema).max(100),
	organizationId: boundedIdentifierSchema,
	userId: boundedIdentifierSchema
});
const disableAgentProfilePrimaryBindingResponseSchema = strictObject({
	agentProfileId: boundedIdentifierSchema,
	disabled: boolean(),
	reasonCodes: array(reasonCodeSchema),
	status: enumFactory([
		"disabled",
		"missing",
		"unavailable"
	])
});
const notifyAgentProfileRouteCatalogChangedBodySchema = strictObject({ reason: string().trim().min(1).max(100).optional() });
const notifyAgentProfilesRouteCatalogChangedBodySchema = strictObject({
	agentProfileIds: array(boundedIdentifierSchema).min(1).max(100),
	reason: string().trim().min(1).max(100).optional()
});
const notifyAgentProfileRouteCatalogChangedResponseSchema = strictObject({
	ok: boolean(),
	notifiedSessions: number().int().nonnegative()
});
const agentProfileBindingStateSchema = enumFactory([
	"missing",
	"enabled_primary",
	"disabled_historical",
	"conflict",
	"owner_mismatch"
]);
const agentProfileBindingResolutionSourceSchema = enumFactory([
	"binding_truth",
	"missing_projection",
	"conflict_evidence"
]);
const agentProfileBindingAuthoritySourceSchema = enumFactory([
	"binding_truth",
	"none",
	"blocked"
]);
const agentProfileBindingAgentControllerReportSchema = strictObject({
	agentControllerRef: boundedIdentifierSchema,
	agentControllerConversationId: boundedIdentifierSchema.optional(),
	openMeldServiceRuntimeId: agentProfileBindingRuntimeIdSchema
});
const agentProfileBindingDispatchEligibilitySchema = strictObject({
	reasonCodes: array(reasonCodeSchema),
	routable: boolean(),
	status: enumFactory(["routable", "not_routable"])
});
const agentProfileBindingProjectionSchema = strictObject({
	agentProfileId: boundedIdentifierSchema,
	authoritySource: agentProfileBindingAuthoritySourceSchema,
	bindingId: boundedIdentifierSchema.optional(),
	bindingState: agentProfileBindingStateSchema,
	dispatchEligibility: agentProfileBindingDispatchEligibilitySchema,
	grantsClaimSuccess: literal(false),
	agentControllerCollaborationMode: agentProfileBindingAgentControllerCollaborationModeSchema.optional(),
	agentControllerPermissionMode: agentProfileBindingAgentControllerPermissionModeSchema.optional(),
	modelSelection: agentProfileBindingModelSelectionSchema.optional(),
	openCodePermission: agentProfileBindingOpenCodePermissionSchema.optional(),
	reasonCodes: array(reasonCodeSchema),
	resolutionSource: agentProfileBindingResolutionSourceSchema,
	routable: boolean(),
	executionTarget: agentProfileBindingAgentControllerReportSchema.nullable()
}).superRefine(validateAgentProfileBindingProjection);
const getAgentProfilePrimaryBindingResponseSchema = strictObject({
	binding: agentProfileBindingProjectionSchema,
	grantsClaimSuccess: literal(false),
	status: enumFactory([
		"found",
		"missing",
		"unavailable"
	])
}).superRefine((value, context) => {
	if (value.binding.bindingState !== "missing") {
		if (value.status !== "found") context.addIssue({
			code: "custom",
			message: "Binding read status must match Binding projection",
			path: ["status"]
		});
		return;
	}
	if (value.status === "found") {
		context.addIssue({
			code: "custom",
			message: "Binding read status must match Binding projection",
			path: ["status"]
		});
		return;
	}
	const unavailable = hasUnavailableBindingReadReason(value.binding.reasonCodes);
	if (value.status === "unavailable" && !unavailable) context.addIssue({
		code: "custom",
		message: "unavailable Binding read requires unavailable reason code",
		path: ["binding", "reasonCodes"]
	});
	if (value.status === "missing" && unavailable) context.addIssue({
		code: "custom",
		message: "unavailable Binding read cannot be reported as missing",
		path: ["status"]
	});
});
const ensureAgentProfilePrimaryBindingResponseSchema = strictObject({
	binding: agentProfileBindingProjectionSchema.optional(),
	grantsClaimSuccess: literal(false),
	outcome: enumFactory([
		"created",
		"reused",
		"reconnected"
	]).optional(),
	reasonCodes: array(reasonCodeSchema),
	routable: boolean(),
	status: enumFactory(["accepted", "rejected"])
}).superRefine((value, context) => {
	if (value.status === "accepted") {
		if (!value.binding) context.addIssue({
			code: "custom",
			message: "accepted Binding command response requires Binding truth",
			path: ["binding"]
		});
		if (!value.outcome) context.addIssue({
			code: "custom",
			message: "accepted Binding command response requires an outcome",
			path: ["outcome"]
		});
		if (!value.routable) context.addIssue({
			code: "custom",
			message: "accepted Binding command response must be routable",
			path: ["routable"]
		});
		if (value.binding) requireAcceptedBindingCommandProjection(value.binding, context);
		return;
	}
	if (value.outcome) context.addIssue({
		code: "custom",
		message: "rejected Binding command response cannot include outcome",
		path: ["outcome"]
	});
	if (value.routable) context.addIssue({
		code: "custom",
		message: "rejected Binding command response cannot be routable",
		path: ["routable"]
	});
});
function validateAgentProfileBindingProjection(value, context) {
	validateBindingDispatchEligibility(value, context);
	bindingProjectionValidators[value.bindingState](value, context);
}
const bindingProjectionValidators = {
	conflict: validateConflictProjection,
	disabled_historical: validateDisabledHistoricalProjection,
	enabled_primary: validateEnabledPrimaryProjection,
	missing: validateMissingProjection,
	owner_mismatch: validateOwnerMismatchProjection
};
function validateBindingDispatchEligibility(value, context) {
	if (value.dispatchEligibility.routable !== value.routable) context.addIssue({
		code: "custom",
		message: "Binding routable fields must agree",
		path: ["dispatchEligibility", "routable"]
	});
	if (value.dispatchEligibility.status !== (value.routable ? "routable" : "not_routable")) context.addIssue({
		code: "custom",
		message: "Binding dispatch status must match routable state",
		path: ["dispatchEligibility", "status"]
	});
}
function validateEnabledPrimaryProjection(value, context) {
	requireBindingTruthProjection(value, context);
	if (!value.routable) context.addIssue({
		code: "custom",
		message: "enabled primary Binding must be routable",
		path: ["routable"]
	});
}
function validateDisabledHistoricalProjection(value, context) {
	requireBindingTruthProjection(value, context);
	if (value.routable) context.addIssue({
		code: "custom",
		message: "disabled historical Binding cannot be routable",
		path: ["routable"]
	});
}
function validateMissingProjection(value, context) {
	if (value.authoritySource !== "none") context.addIssue({
		code: "custom",
		message: "missing Binding projection cannot have authority source",
		path: ["authoritySource"]
	});
	if (value.resolutionSource !== "missing_projection") context.addIssue({
		code: "custom",
		message: "missing Binding projection requires missing source",
		path: ["resolutionSource"]
	});
	rejectMissingProjectionEvidence(value, context);
}
function rejectMissingProjectionEvidence(value, context) {
	if (value.bindingId) context.addIssue({
		code: "custom",
		message: "missing Binding projection cannot include bindingId",
		path: ["bindingId"]
	});
	if (value.executionTarget) context.addIssue({
		code: "custom",
		message: "missing Binding projection cannot include Agent Controller availability",
		path: ["executionTarget"]
	});
	if (value.modelSelection) context.addIssue({
		code: "custom",
		message: "missing Binding projection cannot include model selection",
		path: ["modelSelection"]
	});
	if (value.openCodePermission) context.addIssue({
		code: "custom",
		message: "missing Binding projection cannot include OpenCode permissions",
		path: ["openCodePermission"]
	});
	if (value.agentControllerPermissionMode) context.addIssue({
		code: "custom",
		message: "missing Binding projection cannot include Agent Controller Permission Mode",
		path: ["agentControllerPermissionMode"]
	});
	if (value.agentControllerCollaborationMode) context.addIssue({
		code: "custom",
		message: "missing Binding projection cannot include Agent Controller Collaboration Mode",
		path: ["agentControllerCollaborationMode"]
	});
	if (value.routable) context.addIssue({
		code: "custom",
		message: "missing Binding projection cannot be routable",
		path: ["routable"]
	});
}
function validateConflictProjection(value, context) {
	if (value.authoritySource !== "blocked") context.addIssue({
		code: "custom",
		message: "conflict Binding projection must be blocked",
		path: ["authoritySource"]
	});
	if (value.resolutionSource !== "conflict_evidence") context.addIssue({
		code: "custom",
		message: "conflict Binding projection requires conflict evidence",
		path: ["resolutionSource"]
	});
	if (value.routable) context.addIssue({
		code: "custom",
		message: "conflict Binding projection cannot be routable",
		path: ["routable"]
	});
}
function validateOwnerMismatchProjection(value, context) {
	if (value.authoritySource !== "blocked") context.addIssue({
		code: "custom",
		message: "owner mismatch Binding projection must be blocked",
		path: ["authoritySource"]
	});
	if (value.routable) context.addIssue({
		code: "custom",
		message: "owner mismatch Binding projection cannot be routable",
		path: ["routable"]
	});
}
function requireBindingTruthProjection(value, context) {
	if (value.authoritySource !== "binding_truth") context.addIssue({
		code: "custom",
		message: "Binding truth projection requires binding authority",
		path: ["authoritySource"]
	});
	if (value.resolutionSource !== "binding_truth") context.addIssue({
		code: "custom",
		message: "Binding truth projection requires binding resolution source",
		path: ["resolutionSource"]
	});
	if (!value.bindingId) context.addIssue({
		code: "custom",
		message: "Binding truth projection requires bindingId",
		path: ["bindingId"]
	});
	if (!value.executionTarget) context.addIssue({
		code: "custom",
		message: "Binding truth projection requires Agent Controller availability",
		path: ["executionTarget"]
	});
}
function requireAcceptedBindingCommandProjection(binding, context) {
	if (binding.bindingState !== "enabled_primary") context.addIssue({
		code: "custom",
		message: "accepted Binding command response requires enabled primary truth",
		path: ["binding", "bindingState"]
	});
	if (binding.authoritySource !== "binding_truth") context.addIssue({
		code: "custom",
		message: "accepted Binding command response requires Binding authority",
		path: ["binding", "authoritySource"]
	});
	if (binding.resolutionSource !== "binding_truth") context.addIssue({
		code: "custom",
		message: "accepted Binding command response requires Binding truth source",
		path: ["binding", "resolutionSource"]
	});
	if (!binding.bindingId) context.addIssue({
		code: "custom",
		message: "accepted Binding command response requires bindingId",
		path: ["binding", "bindingId"]
	});
	if (!binding.executionTarget) context.addIssue({
		code: "custom",
		message: "accepted Binding command response requires Agent Controller availability",
		path: ["binding", "executionTarget"]
	});
	if (!(binding.routable && binding.dispatchEligibility.routable)) context.addIssue({
		code: "custom",
		message: "accepted Binding command response requires routable Binding truth",
		path: ["binding", "routable"]
	});
	if (binding.dispatchEligibility.status !== "routable") context.addIssue({
		code: "custom",
		message: "accepted Binding command response requires routable status",
		path: [
			"binding",
			"dispatchEligibility",
			"status"
		]
	});
}
function hasUnavailableBindingReadReason(reasonCodes) {
	return reasonCodes.some((reasonCode) => reasonCode === "agent_profile_binding.lookup_failed" || reasonCode === "agent_profile_binding.owner_mismatch" || reasonCode === "agent_profile_binding.profile_owner_invalid" || reasonCode === "agent_profile_binding.profile_owner_unavailable" || reasonCode === "agent_profile_binding.read_unavailable" || reasonCode === "agent_profile_binding.store_unavailable");
}
//#endregion
export { agentProfileBindingAgentControllerCollaborationModeSchema, agentProfileBindingAgentControllerPermissionModeSchema, agentProfileBindingAgentControllerReportSchema, agentProfileBindingAuthoritySourceSchema, agentProfileBindingDispatchEligibilitySchema, agentProfileBindingModelSelectionSchema, agentProfileBindingOpenCodePermissionSchema, agentProfileBindingProjectionSchema, agentProfileBindingResolutionSourceSchema, agentProfileBindingRouteParamsSchema, agentProfileBindingRuntimeIdSchema, agentProfileBindingSessionInventorySelectionSchema, agentProfileBindingStateSchema, agentProfileBindingWriteModeSchema, agentProfileCreationDefaultsSchema, agentProfileCreationPreferenceSelectionSchema, currentAgentProfileSessionLookupQuerySchema, currentAgentProfileSessionLookupResponseSchema, disableAgentProfilePrimaryBindingResponseSchema, ensureAgentProfilePrimaryBindingBodySchema, ensureAgentProfilePrimaryBindingResponseSchema, getAgentProfileControllerChangeEligibilityResponseSchema, getAgentProfileCreationDefaultsResponseSchema, getAgentProfilePrimaryBindingResponseSchema, listAgentProfileCreationDefaultsResponseSchema, notifyAgentProfileRouteCatalogChangedBodySchema, notifyAgentProfileRouteCatalogChangedResponseSchema, notifyAgentProfilesRouteCatalogChangedBodySchema, setAgentProfileCreationDefaultsBodySchema, setAgentProfileCreationDefaultsResponseSchema, updateAgentProfilePrimaryBindingAgentControllerCollaborationModeBodySchema, updateAgentProfilePrimaryBindingAgentControllerPermissionModeBodySchema, updateAgentProfilePrimaryBindingModelSelectionBodySchema, updateAgentProfilePrimaryBindingOpenCodePermissionBodySchema };

//# sourceMappingURL=agent-profile-binding.js.map